// SS-4462: price wording shared by the Subscription page and the superadmin Bundles /
// Add-ons tools. Generic on purpose — it reads the Stripe price (and its product's
// unit_label) and never a product name, so the Providers price, Phone Number's tiers and
// any future licensed tiered price are all described the same way.

// A Stripe Price carries its usage type under `recurring`; the catalog
// (GET /settings/subscriptions availableProducts) flattens it to `usage_type`.
export const usageTypeOf = (plan) => plan?.recurring?.usage_type ?? plan?.usage_type;

export const isLicensedPrice = (plan) => usageTypeOf(plan) === "licensed";

export const isTieredPrice = (plan) =>
  plan?.billing_scheme === "tiered" && Array.isArray(plan?.tiers) && plan.tiers.length > 0;

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// A tier or price amount in cents: the integer field, else the decimal string.
const centsOf = (amount, decimal) => toNumber(amount) ?? toNumber(decimal) ?? 0;

export const unitAmountOf = (plan) => centsOf(plan?.unit_amount ?? plan?.amount, plan?.unit_amount_decimal);

// Whole dollars without decimals ($350), cents with two ($10.50), and sub-cent prices
// with as many digits as they need ($0.016).
export const formatMoney = (cents) => {
  const dollars = cents / 100;
  if (Number.isInteger(dollars)) {
    return `$${dollars.toLocaleString("en-US")}`;
  }
  const twoDigits = Math.round(dollars * 100) / 100;
  if (Math.abs(twoDigits - dollars) < 1e-9) {
    return `$${dollars.toFixed(2)}`;
  }
  return `$${Number(dollars.toFixed(4))}`;
};

// A price that costs nothing at any quantity: a $0 per-unit price, or tiers that are all $0.
export const isZeroPrice = (plan) => {
  if (!plan) {
    return false;
  }
  if (isTieredPrice(plan)) {
    return plan.tiers.every(
      (tier) => centsOf(tier.unit_amount, tier.unit_amount_decimal) === 0 &&
        centsOf(tier.flat_amount, tier.flat_amount_decimal) === 0
    );
  }
  return unitAmountOf(plan) === 0;
};

const intervalSuffix = (plan) => {
  const interval = plan?.recurring?.interval ?? plan?.interval ?? "month";
  return interval && interval !== "non-recurring" ? `/${interval}` : "";
};

const pluralise = (unit) => (/(s|x|ch|sh)$/.test(unit) ? `${unit}es` : `${unit}s`);

const upToOf = (tier) =>
  tier?.up_to === null || tier?.up_to === undefined || tier?.up_to === "inf" ? Infinity : Number(tier.up_to);

// A volume price: every unit bills at the one tier the quantity lands in.
function describeVolumeTiers(tiers, unit, per) {
  const count = (n) => (n === 1 ? `1 ${unit}` : `${n} ${pluralise(unit)}`);
  const phrases = tiers.map((tier, index) => {
    const previous = index === 0 ? 0 : upToOf(tiers[index - 1]);
    const upTo = upToOf(tier);
    let range;
    if (upTo === Infinity) {
      range = index === 0 ? `any number of ${pluralise(unit)}` : `${previous + 1} or more ${pluralise(unit)}`;
    } else if (previous + 1 === upTo) {
      range = count(upTo);
    } else {
      range = index === 0 ? `up to ${count(upTo)}` : `${previous + 1}–${upTo} ${pluralise(unit)}`;
    }
    const unitCents = centsOf(tier.unit_amount, tier.unit_amount_decimal);
    const flatCents = centsOf(tier.flat_amount, tier.flat_amount_decimal);
    if (!unitCents && !flatCents) {
      return `${range} included`;
    }
    const parts = [
      ...(flatCents ? [`${formatMoney(flatCents)}${per}`] : []),
      ...(unitCents ? [`${formatMoney(unitCents)}${per} per ${unit}`] : []),
    ];
    return `${parts.join(" plus ")} for ${range}`;
  });
  const text = phrases.join(", ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A licensed tiered price in plain words, from its tiers:
 *   [{up_to:1, 35000}, {up_to:inf, 15000}] →
 *     "$350/month for the first provider, $150/month for each additional provider"
 *   [{up_to:10, $0}, {up_to:inf, 1000}] →
 *     "The first 10 phone numbers included, $10/month for each additional phone number"
 * A scaled group price (up_to multiplied by the locations) reads
 * "$350/month each for the first N providers". A VOLUME price bills every unit at the one
 * tier the quantity lands in, and says so: "$350/month each for 1 provider, $150/month each
 * for 2 or more providers".
 */
export const describeLicensedTiers = (plan, unitLabel) => {
  const tiers = plan?.tiers || [];
  const unit = unitLabel || "unit";
  const per = intervalSuffix(plan);
  if (plan?.tiers_mode === "volume") {
    return describeVolumeTiers(tiers, unit, per);
  }
  const phrases = tiers.map((tier, index) => {
    const previous = index === 0 ? 0 : upToOf(tiers[index - 1]);
    const upTo = upToOf(tier);
    let range;
    if (upTo === Infinity) {
      range = index === 0 ? `each ${unit}` : `each additional ${unit}`;
    } else if (index === 0) {
      range = upTo === 1 ? `the first ${unit}` : `the first ${upTo} ${pluralise(unit)}`;
    } else if (upTo === previous + 1) {
      range = `${unit} ${upTo}`;
    } else {
      range = `${pluralise(unit)} ${previous + 1}–${upTo}`;
    }
    const unitCents = centsOf(tier.unit_amount, tier.unit_amount_decimal);
    const flatCents = centsOf(tier.flat_amount, tier.flat_amount_decimal);
    if (!unitCents && !flatCents) {
      return `${range} included`;
    }
    // a flat amount is charged once for the tier, so an open tier reads "any additional
    // providers" rather than "each additional provider", and a later one says "flat"
    const flatRange = upTo === Infinity
      ? (index === 0 ? `any number of ${pluralise(unit)}` : `any additional ${pluralise(unit)}`)
      : range;
    const flat = `${formatMoney(flatCents)}${per}${index === 0 ? "" : " flat"}`;
    if (!unitCents) {
      return `${flat} for ${flatRange}`;
    }
    // a per-unit rate over a range of several units is per unit, not for the range
    const each = upTo !== Infinity && upTo - previous > 1 ? " each" : "";
    if (!flatCents) {
      return `${formatMoney(unitCents)}${per}${each} for ${range}`;
    }
    return `${formatMoney(flatCents)}${per} flat plus ${formatMoney(unitCents)}${per} per ${unit} for ${flatRange}`;
  });
  const text = phrases.join(", ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};


// The licenses a location subscribing to the providers product needs by default: the
// group's activated providers not already covered by the other locations' licenses, and
// at least one. A single location (or a group without the product yet) needs `used`.
export const defaultProviderLicenseAmount = (providerLicenses) => {
  const used = providerLicenses?.used || 0;
  const elsewhere = providerLicenses && !providerLicenses.unlimited && providerLicenses.capacity !== null &&
    providerLicenses.capacity !== undefined
    ? Math.max(providerLicenses.capacity - (providerLicenses.location_quantity || 0), 0)
    : 0;
  return Math.max(1, used - elsewhere);
};

// The products that sell provider licences: their features INCLUDE `providers`. A raw
// Stripe product (bundle items) carries them comma separated in metadata — its own
// `features` array is Stripe's marketing features, normally [] — so metadata wins when it is
// there; the catalog (availableProducts) has no metadata and an array of our features.
export const isProviderLicenseProduct = (product) => {
  const features = product?.metadata
    ? String(product.metadata.features || "").split(",")
    : (Array.isArray(product?.features) ? product.features : []);
  return features.includes("providers");
};

// "3 of 5 provider licenses in use", or null when the group is unlimited / unknown.
export const describeProviderLicenses = (providerLicenses) => {
  if (!providerLicenses || providerLicenses.unlimited || providerLicenses.capacity === null ||
    providerLicenses.capacity === undefined) {
    return null;
  }
  const capacity = providerLicenses.capacity;
  return `${providerLicenses.used ?? 0} of ${capacity} provider license${capacity === 1 ? "" : "s"} in use`;
};

// The provider-license capacity refusal the api codes (essentials/config/errors.js
// PROVIDER_LICENSE_LIMIT), so the UI can point the admin to Settings → Subscription.
export const PROVIDER_LICENSE_LIMIT_CODE = 202;
export const isProviderLicenseLimitError = (response) =>
  response?.type === "error" && Number(response?.code) === PROVIDER_LICENSE_LIMIT_CODE;

// What a licensed tiered price bills for `quantity` units (cents): graduated bills each
// tier's units at its rate (plus its flat amount once reached), volume bills every unit at
// the one tier the quantity lands in. Mirrors the api's licensedTieredAmount.
export const licensedTieredAmount = (plan, quantity) => {
  const tiers = plan?.tiers || [];
  const units = Number(quantity) || 0;
  if (!tiers.length || units <= 0) {
    return 0;
  }
  const tierCost = (tier, count) =>
    count * centsOf(tier.unit_amount, tier.unit_amount_decimal) + centsOf(tier.flat_amount, tier.flat_amount_decimal);
  if (plan.tiers_mode === "volume") {
    return tierCost(tiers.find((tier) => units <= upToOf(tier)) || tiers[tiers.length - 1], units);
  }
  let total = 0;
  let previous = 0;
  for (const tier of tiers) {
    if (units <= previous) {
      break;
    }
    total += tierCost(tier, Math.min(units, upToOf(tier)) - previous);
    previous = upToOf(tier);
  }
  return total;
};

// An add-on is subscribed once by each location (each item at a quantity of one per
// location, which the api's group handling sums), so a licensed per-unit add-on price is a
// price PER LOCATION, whatever the product's unit label says — e.g. Full Platform's $300.
export const perLocationPriceLabel = (plan) =>
  `${formatMoney(unitAmountOf(plan))}${intervalSuffix(plan)} per location`;

export const isPerLocationAddonPrice = (plan) =>
  isLicensedPrice(plan) && plan?.billing_scheme === "per_unit" && !isZeroPrice(plan);

// Superadmin Bundles / Add-ons tools: the label a price gets in the products list.
export const adminPriceLabel = (product, price, { addon = false } = {}) => {
  if (addon && isPerLocationAddonPrice(price)) {
    return perLocationPriceLabel(price);
  }
  if (price?.billing_scheme === "per_unit") {
    return `$${price.unit_amount / 100} per ${product?.unit_label}`;
  }
  if (isLicensedPrice(price) && isTieredPrice(price)) {
    return "Licensed (tiered)";
  }
  return "Metered";
};

// ...and what it adds to their "Fixed Price" total (dollars): per-unit prices as before,
// licensed tiered prices at a quantity of one. Metered usage is not a fixed price.
export const adminFixedPriceOf = (price) => {
  if (price?.billing_scheme === "per_unit") {
    return price.unit_amount / 100;
  }
  if (isLicensedPrice(price) && isTieredPrice(price)) {
    return licensedTieredAmount(price, 1) / 100;
  }
  return 0;
};
